import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import translations from '../i18n/translations';

const STORAGE_KEY = 'app_language';
const LanguageContext = createContext(null);

const looksLikeBrokenUtf8 = value => /(?:Ã.|Â.|â.|ðŸ|à[¤¥])/.test(String(value || ''));

const repairBrokenUtf8 = value => {
  const text = String(value || '');
  if (!looksLikeBrokenUtf8(text)) return text;
  try {
    const encoded = Array.from(text).map(char => {
      const code = char.charCodeAt(0);
      return code <= 255 ? `%${code.toString(16).padStart(2, '0')}` : char;
    }).join('');
    return decodeURIComponent(encoded);
  } catch (error) {
    return text;
  }
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'hi' ? 'hi' : 'en';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language === 'hi' ? 'hi' : 'en';
  }, [language]);

  const t = useMemo(
    () => (key, vars = {}) => {
      const current = translations[language] || translations.en;
      const template = repairBrokenUtf8(current[key] || translations.en[key] || key);
      return template.replace(/\{\{(\w+)\}\}/g, (_, token) => vars[token] ?? '');
    },
    [language]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      locale: language === 'hi' ? 'hi-IN' : 'en-US',
      t,
    }),
    [language, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}
